/**
 * Pooled particle system.
 *
 * The server sends compact effect records; everything spectacular happens here,
 * on the client, where it costs the simulation nothing.
 */

import { Container, Sprite } from 'pixi.js';
import { ART, Fx } from '../../shared/constants';
import { arrowTex, particleTex, ringTex } from './sprites';

interface P {
  s: Sprite;
  vx: number;
  vy: number;
  life: number;
  max: number;
  grow: number;
  drag: number;
  gravity: number;
  spin: number;
}

const MAX_PARTICLES = 1600;

export class Particles {
  readonly container = new Container();
  private pool: Sprite[] = [];
  private live: P[] = [];
  /** where the next cull lands when the pool is full — see `add` */
  private cull = 0;

  constructor() {
    this.container.eventMode = 'none';
  }

  private take(tex = particleTex): Sprite {
    let s = this.pool.pop();
    if (!s) {
      s = new Sprite(tex);
      s.anchor.set(0.5);
      this.container.addChild(s);
    }
    s.texture = tex;
    s.visible = true;
    s.alpha = 1;
    s.rotation = 0;
    s.scale.set(1);
    return s;
  }

  /**
   * Add a particle, retiring one if the pool is already full.
   *
   * This used to be `live.shift()`, which memmoves the whole array — and it ran
   * **once per spawn** for as long as the pool stayed full, which is precisely
   * the moment a battle is at its worst. One meteor asks for 42 particles and a
   * tick may carry `MAX_EFFECTS_PER_TICK` of them, so a heavy frame was moving
   * millions of slots for nothing.
   *
   * A rotating cursor is O(1) and has the better failure mode of the two
   * alternatives: refusing the new particle would make a fresh burst invisible
   * behind a screen of old smoke, and always taking index 0 would concentrate
   * the culling on whatever happens to be at the front. This spreads it.
   */
  private add(p: P) {
    if (this.live.length >= MAX_PARTICLES) {
      this.cull = (this.cull + 1) % this.live.length;
      const dead = this.live[this.cull];
      dead.s.visible = false;
      this.pool.push(dead.s);
      this.live[this.cull] = p;
      return;
    }
    this.live.push(p);
  }

  spawn(tex: any, x: number, y: number, color: number, opts: Partial<P> & { size?: number; alpha?: number } = {}) {
    const s = this.take(tex);
    s.position.set(x, y);
    s.tint = color;
    s.scale.set(opts.size ?? 1);
    s.alpha = opts.alpha ?? 1;
    this.add({
      s,
      vx: opts.vx ?? 0,
      vy: opts.vy ?? 0,
      life: opts.life ?? 0.5,
      max: opts.life ?? 0.5,
      grow: opts.grow ?? 0,
      drag: opts.drag ?? 0.9,
      gravity: opts.gravity ?? 0,
      spin: opts.spin ?? 0,
    });
  }

  burst(x: number, y: number, color: number, n: number, speed: number, life = 0.5, size = 1) {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const v = speed * (0.4 + Math.random() * 0.8);
      this.spawn(particleTex, x, y, color, {
        vx: Math.cos(a) * v,
        vy: Math.sin(a) * v,
        life: life * (0.6 + Math.random() * 0.7),
        size,
        drag: 0.88,
      });
    }
  }

  ring(x: number, y: number, color: number, grow: number, life: number, size = 1) {
    this.spawn(ringTex, x, y, color, { life, grow, size, drag: 1 });
  }

  streak(x0: number, y0: number, x1: number, y1: number, color: number, speed: number, tex = arrowTex) {
    const dx = x1 - x0, dy = y1 - y0;
    const d = Math.hypot(dx, dy) || 1;
    const life = Math.min(0.45, d / speed);
    const s = this.take(tex);
    s.position.set(x0, y0);
    s.tint = color;
    s.rotation = Math.atan2(dy, dx);
    s.scale.set(1.2);
    this.add({
      s,
      vx: (dx / d) * speed,
      vy: (dy / d) * speed,
      life,
      max: life,
      grow: 0,
      drag: 1,
      gravity: 0,
      spin: 0,
    });
  }

  /** Translates a server effect record into visuals. */
  effect(type: number, tx: number, ty: number, sx: number, sy: number, param: number, color: number) {
    const x = tx * ART, y = ty * ART;
    const ox = sx * ART, oy = sy * ART;

    switch (type) {
      case Fx.Hit:
        this.burst(x, y, 0xfff2c4, 5, 26, 0.28, 1);
        this.spawn(particleTex, x, y, 0xffffff, { life: 0.12, size: 2, drag: 1 });
        break;
      case Fx.Arrow:
        this.streak(ox, oy, x, y, 0xe9dcc0, 260);
        this.burst(x, y, 0xd9cba8, 3, 18, 0.22, 1);
        break;
      case Fx.Fireball:
        this.streak(ox, oy, x, y, 0xffa63d, 200);
        this.burst(x, y, 0xff8a3d, 8, 30, 0.4, 1.2);
        this.ring(x, y, 0xffd06a, 22, 0.28, 0.5);
        break;
      case Fx.Meteor:
        this.ring(x, y, 0xffb347, 90, 0.55, 0.6);
        this.ring(x, y, 0xff5a2b, 60, 0.75, 0.4);
        this.burst(x, y, 0xff7a2b, 26, 55, 0.7, 1.6);
        this.burst(x, y, 0x66422a, 14, 32, 0.9, 1.2);
        break;
      case Fx.Zap: {
        const seg = 4;
        for (let i = 0; i <= seg; i++) {
          const t = i / seg;
          const jx = (Math.random() - 0.5) * 6;
          const jy = (Math.random() - 0.5) * 6;
          this.spawn(particleTex, ox + (x - ox) * t + jx, oy + (y - oy) * t + jy, 0x9fe4ff, {
            life: 0.18, size: 1.4, drag: 1,
          });
        }
        this.burst(x, y, 0xbdefff, 6, 24, 0.3, 1);
        break;
      }
      case Fx.Death:
        this.burst(x, y, color, 14, 34, 0.6, 1.3);
        this.burst(x, y, 0x1a1420, 6, 18, 0.5, 1);
        break;
      case Fx.Capture:
        this.spawn(particleTex, x, y, color, { life: 0.5, vy: -12, size: 1.2, drag: 0.94 });
        break;
      case Fx.Build:
        this.burst(x, y, 0xd8c9a8, 12, 28, 0.6, 1.2);
        this.ring(x, y, 0xffffff, 26, 0.3, 0.4);
        break;
      /*
       * `Fx.Steal` used to be drawn here. It went with the thieves and the
       * server no longer emits it; the enum value stays put so the numbers
       * after it do not move.
       */
      /*
       * A druid mending somebody. The server sends the patient as the target
       * and the healer as the origin, so the thread runs the right way round.
       *
       * This was emitted from the very first day the Druid existed and drawn on
       * none of them — the elven healer's whole contribution happened in
       * silence, which is why it read as a unit that does nothing.
       */
      case Fx.Heal:
        this.streak(ox, oy, x, y, 0x9ff26a, 150);
        this.ring(x, y, 0x9ff26a, 20, 0.35, 0.35);
        for (let i = 0; i < 6; i++) {
          this.spawn(particleTex, x + (Math.random() - 0.5) * 6, y + 3, 0xd6ffb0, {
            vy: -18 - Math.random() * 16,
            life: 0.6,
            size: 1,
            drag: 0.97,
          });
        }
        break;
      /*
       * A phoenix getting back up. Same story as `Heal`: emitted, never drawn,
       * so the one thing that makes the elven elite worth its mana looked
       * exactly like a unit that had simply not died.
       *
       * A hard white flash first — that is the beat that reads as *again* — and
       * then fire climbing off the bird.
       */
      case Fx.Rebirth:
        this.ring(x, y, 0xfff2c4, 74, 0.35, 0.5);
        this.ring(x, y, 0xff8a3d, 48, 0.6, 0.4);
        this.burst(x, y, 0xffb347, 18, 40, 0.6, 1.3);
        for (let i = 0; i < 14; i++) {
          this.spawn(particleTex, x + (Math.random() - 0.5) * 10, y + 2, 0xffd06a, {
            vy: -40 - Math.random() * 46,
            life: 0.9,
            size: 1.4,
            drag: 0.95,
          });
        }
        break;
      /*
       * A kingdom ending, at its capital.
       *
       * Slow and large on purpose: every other effect here marks something that
       * happened on one tile, and this one marks a whole realm. Ash and ember
       * are hard-coded rather than taken from `color`, because the ground under
       * it went back to the wilderness in the same tick and `color` would
       * therefore be the bone white of unowned land.
       */
      case Fx.Downfall:
        this.ring(x, y, 0x2a2430, 120, 0.9, 0.9);
        this.ring(x, y, 0xff6b35, 86, 0.7, 0.6);
        this.burst(x, y, 0x6b5f52, 22, 30, 1.4, 2);
        for (let i = 0; i < 16; i++) {
          this.spawn(particleTex, x + (Math.random() - 0.5) * 14, y, 0xff8a3d, {
            vx: (Math.random() - 0.5) * 14,
            vy: -26 - Math.random() * 34,
            life: 1.3,
            size: 1.2,
            drag: 0.96,
          });
        }
        break;
      /*
       * Somebody's opening truce lapsing. A door opening, not an explosion: two
       * wide rings and nothing thrown, in the realm's own colour — the capital
       * is still theirs when this lands, so `color` is right here.
       */
      case Fx.Unshielded:
        this.ring(x, y, color, 96, 0.75, 0.35);
        this.ring(x, y, 0xffffff, 58, 0.45, 0.25);
        break;
      case Fx.Raze:
        this.burst(x, y, 0xff6b35, 8, 22, 0.5, 1.3);
        this.spawn(particleTex, x, y, 0x3a3038, { life: 0.9, vy: -14, size: 2, drag: 0.95 });
        break;
      case Fx.Spawn:
        this.ring(x, y, color, 60, 0.6, 0.5);
        this.burst(x, y, color, 16, 30, 0.7, 1.2);
        break;
      case Fx.StructureDown:
        this.burst(x, y, 0x9c9484, 18, 36, 0.8, 1.5);
        this.burst(x, y, 0x2a2430, 10, 20, 1.1, 2);
        this.ring(x, y, 0xffb347, 40, 0.4, 0.5);
        break;
      case Fx.Trade:
        // coins tumbling out of an arriving caravan
        for (let i = 0; i < 8; i++) {
          this.spawn(particleTex, x, y, 0xffd85e, {
            vx: (Math.random() - 0.5) * 26,
            vy: -16 - Math.random() * 18,
            gravity: 70,
            life: 0.8,
            size: 1.2,
            drag: 0.97,
          });
        }
        this.ring(x, y, 0xffd85e, 26, 0.4, 0.4);
        break;
      case Fx.Loot:
        this.burst(x, y, 0xffd85e, 16, 40, 0.9, 1.4);
        this.ring(x, y, 0xff6b35, 34, 0.45, 0.5);
        break;
      case Fx.Clash:
        // two hosts meeting in the field: a hard shock ring and a lot of dust
        this.ring(x, y, 0xffe9a8, 70, 0.45, 0.7);
        this.ring(x, y, 0xff6b35, 44, 0.6, 0.5);
        this.burst(x, y, 0xfff2c4, 22, 46, 0.7, 1.4);
        this.burst(x, y, 0x6b5f52, 14, 26, 1.1, 2);
        break;
      /*
       * One strike of a race power. `param` carries the race, so the same
       * machine reads as three different catastrophes: human meteors fall as
       * burning rock, the dwarven Colossus lands as a shock of stone and rune
       * light, and the elven Wrath blooms as green fire.
       */
      case Fx.Wrath: {
        const pal = param === 1 ? [0x9fd6ff, 0xbfc7d4, 0x5a6270]
          : param === 2 ? [0x9ff26a, 0x4f9e4a, 0x2c6330]
            : [0xffb347, 0xff5a2b, 0x66422a];
        this.streak(ox, oy, x, y, pal[0], 260);
        this.ring(x, y, pal[0], 62, 0.45, 0.55);
        this.ring(x, y, pal[1], 40, 0.6, 0.4);
        this.burst(x, y, pal[1], 20, 48, 0.65, 1.5);
        this.burst(x, y, pal[2], 12, 28, 0.95, 1.2);
        break;
      }
      /*
       * The Colossus itself, standing where it stands.
       *
       * Emitted every step of its march rather than at the end of one, so the
       * overlapping rings read as one machine walking across the province
       * instead of a string of unrelated flashes. Deliberately far larger than
       * any strike: it is twenty-two seconds of something that cost thirty-six
       * thousand, and it should be obvious from across the map which way it is
       * heading. Rune-blue over stone grey, so it can never be mistaken for the
       * human meteors or the elven green fire.
       */
      case Fx.Colossus: {
        this.ring(x, y, 0x9fd6ff, 104, 0.6, 1.05);
        this.ring(x, y, 0x6f7c8c, 78, 0.5, 0.85);
        this.ring(x, y, 0xe6f4ff, 38, 0.3, 1.25);
        // rune light streaming off the body, so it reads as lit from within
        for (let i = 0; i < 12; i++) {
          const a = Math.random() * Math.PI * 2;
          this.spawn(particleTex, x + Math.cos(a) * 9, y + Math.sin(a) * 9, 0x9fd6ff, {
            vy: -34 - Math.random() * 44,
            life: 0.95,
            size: 2.1,
            drag: 0.94,
          });
        }
        // and the ground it just put its foot through
        this.burst(x, y, 0x5a6270, 14, 42, 0.75, 2);
        break;
      }
      case Fx.Upgrade:
        this.ring(x, y, 0xfff0c0, 34, 0.5, 0.4);
        for (let i = 0; i < 10; i++) {
          this.spawn(particleTex, x + (Math.random() - 0.5) * 8, y + 4, 0xffe9a8, {
            vy: -20 - Math.random() * 20,
            life: 0.7,
            size: 1.1,
            drag: 0.98,
          });
        }
        break;
    }
  }

  update(dt: number) {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const p = this.live[i];
      p.life -= dt;
      if (p.life <= 0) {
        p.s.visible = false;
        this.pool.push(p.s);
        /*
         * Swap-and-pop, not `splice`. Splice shifts every element above the
         * hole, and hundreds of particles expire on the same frame in a real
         * battle, so the old code cost O(n²) exactly when the frame was already
         * full. Walking downwards is what makes the swap safe: whatever comes
         * down from the end has already been stepped this frame, so landing it
         * in a slot we are past cannot step it twice or skip it.
         */
        const last = this.live.pop()!;
        if (i < this.live.length) this.live[i] = last;
        continue;
      }
      p.vy += p.gravity * dt;
      p.s.x += p.vx * dt;
      p.s.y += p.vy * dt;
      p.vx *= Math.pow(p.drag, dt * 60);
      p.vy *= Math.pow(p.drag, dt * 60);
      if (p.grow) p.s.scale.set(p.s.scale.x + p.grow * dt * 0.1);
      if (p.spin) p.s.rotation += p.spin * dt;
      p.s.alpha = Math.min(1, (p.life / p.max) * 1.6);
    }
  }

  get count() { return this.live.length; }
}
